import React, { createContext, useContext, useState, useEffect } from 'react'
import alumniService from '../api/alumniService'
import { useAuth } from '../context/AuthContext'

const AlumniContext = createContext()

export const AlumniProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const [successStories, setSuccessStories] = useState([])
  const [testimonials, setTestimonials] = useState([])
  const [statistics, setStatistics] = useState([])
  const [registrations, setRegistrations] = useState([])
  const [storySubmissions, setStorySubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load success stories from backend
  useEffect(() => {
    let isMounted = true; // Cleanup flag to prevent state updates after unmount

    const fetchSuccessStories = async () => {
      try {
        setLoading(true)
        setError(null)
        // Fetch all stories (both active and inactive) for admin management
        const response = await alumniService.getSuccessStories({ isActive: 'all' })
        
        if (isMounted && response.status === 'success' && response.data?.stories) {
          setSuccessStories(response.data.stories)
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching success stories:', err)
        }
        if (isMounted) {
          setError(err.response?.data?.message || 'Failed to load success stories')
          setSuccessStories([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchSuccessStories()

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [])

  // Load testimonials from backend
  useEffect(() => {
    let isMounted = true;

    const fetchTestimonials = async () => {
      try {
        const response = await alumniService.getTestimonials()
        
        if (isMounted && response.status === 'success' && response.data?.testimonials) {
          setTestimonials(response.data.testimonials)
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching testimonials:', err)
        }
        if (isMounted) {
          setTestimonials([])
        }
      }
    }

    fetchTestimonials()

    return () => {
      isMounted = false;
    };
  }, [])

  // Load statistics from backend
  useEffect(() => {
    let isMounted = true;

    const fetchStatistics = async () => {
      try {
        const response = await alumniService.getStatistics()
        
        if (isMounted && response.status === 'success' && response.data?.statistics) {
          setStatistics(response.data.statistics)
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching statistics:', err)
        }
        if (isMounted) {
          setStatistics([])
        }
      }
    }

    fetchStatistics()

    return () => {
      isMounted = false;
    };
  }, [])

  // Load registrations from backend (admin only - requires authentication)
  useEffect(() => {
    let isMounted = true;

    // Only fetch registrations if user is authenticated
    if (!isAuthenticated) {
      setRegistrations([])
      return
    }

    const fetchRegistrations = async () => {
      try {
        const response = await alumniService.getRegistrations()
        
        if (isMounted && response.status === 'success' && response.data?.registrations) {
          setRegistrations(response.data.registrations)
        }
      } catch (err) {
        // Only log error if not authentication issue (admin only)
        if (import.meta.env.DEV && err.response?.status !== 401 && err.response?.status !== 403) {
          console.error('Error fetching registrations:', err)
        }
        if (isMounted) {
          setRegistrations([])
        }
      }
    }

    fetchRegistrations()

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated])

  // Filter story submissions from success stories (isActive: false)
  useEffect(() => {
    const submissions = successStories.filter(story => story.isActive === false)
    setStorySubmissions(submissions)
  }, [successStories])

  // =============== SUCCESS STORIES CRUD ===============

  const addSuccessStory = async (storyData) => {
    try {
      // Create FormData for image upload
      const formData = new FormData()
      
      // Add all text fields
      Object.keys(storyData).forEach(key => {
        if (key === 'image' && storyData[key] instanceof File) {
          formData.append('image', storyData[key])
        } else if (key === 'achievements' && Array.isArray(storyData[key])) {
          formData.append('achievements', storyData[key].join(','))
        } else if (storyData[key] !== null && storyData[key] !== undefined) {
          formData.append(key, storyData[key])
        }
      })

      const response = await alumniService.createSuccessStory(formData)
      
      if (response.status === 'success' && response.data?.story) {
        setSuccessStories(prev => [...prev, response.data.story])
        return response.data.story
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error adding success story:', err)
      throw err
    }
  }

  const updateSuccessStory = async (id, updates) => {
    try {
      // Create FormData if there's an image
      let requestData = new FormData()
      
      // Filter out MongoDB internal fields - only send valid schema fields
      const validFields = ['name', 'batchYear', 'title', 'story', 'currentPosition', 'company', 'achievements', 'isActive', 'featured', 'order']
      
      if (updates.image instanceof File) {
        Object.keys(updates).forEach(key => {
          if (key === 'image') {
            requestData.append('image', updates[key])
          } else if (key === 'achievements' && Array.isArray(updates[key])) {
            requestData.append('achievements', updates[key].join(','))
          } else if (validFields.includes(key) && updates[key] !== null && updates[key] !== undefined) {
            // Only send valid schema fields, exclude _id, __v, createdAt, updatedAt, etc.
            requestData.append(key, updates[key])
          }
        })
      } else {
        Object.keys(updates).forEach(key => {
          if (key === 'achievements' && Array.isArray(updates[key])) {
            requestData.append('achievements', updates[key].join(','))
          } else if (validFields.includes(key) && updates[key] !== null && updates[key] !== undefined) {
            // Only send valid schema fields, exclude _id, __v, createdAt, updatedAt, etc.
            requestData.append(key, updates[key])
          }
        })
      }

      const response = await alumniService.updateSuccessStory(id, requestData)
      
      if (response.status === 'success' && response.data?.story) {
        setSuccessStories(prev => 
          prev.map(story => story._id === id ? response.data.story : story)
        )
        return response.data.story
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error updating success story:', err)
      throw err
    }
  }

  const deleteSuccessStory = async (id) => {
    try {
      await alumniService.deleteSuccessStory(id)
      setSuccessStories(prev => prev.filter(story => story._id !== id))
    } catch (err) {
      console.error('Error deleting success story:', err)
      throw err
    }
  }

  // Approve a story submission (set isActive to true)
  const approveStorySubmission = async (id) => {
    try {
      const response = await alumniService.approveStory(id)
      
      if (response.status === 'success' && response.data?.story) {
        // Update success stories state (which will trigger storySubmissions update)
        setSuccessStories(prev => 
          prev.map(story => story._id === id ? response.data.story : story)
        )
        return response
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error approving story:', err)
      throw err
    }
  }

  // =============== TESTIMONIALS CRUD ===============

  const addTestimonial = async (testimonialData) => {
    try {
      const formData = new FormData()
      
      Object.keys(testimonialData).forEach(key => {
        if (key === 'image' && testimonialData[key] instanceof File) {
          formData.append('image', testimonialData[key])
        } else if (testimonialData[key] !== null && testimonialData[key] !== undefined) {
          formData.append(key, testimonialData[key])
        }
      })

      const response = await alumniService.submitTestimonial(formData)
      
      if (response.status === 'success' && response.data?.testimonial) {
        setTestimonials(prev => [...prev, response.data.testimonial])
        return response.data.testimonial
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error adding testimonial:', err)
      throw err
    }
  }

  const updateTestimonial = async (id, updates) => {
    try {
      const response = await alumniService.updateTestimonial(id, updates)
      
      if (response.status === 'success' && response.data?.testimonial) {
        setTestimonials(prev => 
          prev.map(testimonial => testimonial._id === id ? response.data.testimonial : testimonial)
        )
        return response.data.testimonial
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error updating testimonial:', err)
      throw err
    }
  }

  const deleteTestimonial = async (id) => {
    try {
      await alumniService.deleteTestimonial(id)
      setTestimonials(prev => prev.filter(testimonial => testimonial._id !== id))
    } catch (err) {
      console.error('Error deleting testimonial:', err)
      throw err
    }
  }

  // =============== STATISTICS CRUD ===============

  const addStatistic = async (statData) => {
    try {
      const response = await alumniService.createStatistic(statData)
      
      if (response.status === 'success' && response.data?.statistic) {
        setStatistics(prev => [...prev, response.data.statistic])
        return response.data.statistic
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error adding statistic:', err)
      throw err
    }
  }

  const updateStatistic = async (id, updates) => {
    try {
      const response = await alumniService.updateStatistic(id, updates)
      
      if (response.status === 'success' && response.data?.statistic) {
        setStatistics(prev => 
          prev.map(stat => stat._id === id ? response.data.statistic : stat)
        )
        return response.data.statistic
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error updating statistic:', err)
      throw err
    }
  }

  const deleteStatistic = async (id) => {
    try {
      await alumniService.deleteStatistic(id)
      setStatistics(prev => prev.filter(stat => stat._id !== id))
    } catch (err) {
      console.error('Error deleting statistic:', err)
      throw err
    }
  }

  // =============== LEGACY METHODS (for backward compatibility) ===============

  // Alumni registration - API call
  const registerAlumni = async (registrationData) => {
    try {
      const response = await alumniService.registerAlumni(registrationData)
      
      if (response.status === 'success') {
        // Optionally add to local state for immediate feedback
        const newRegistration = {
          ...registrationData,
          id: Date.now(),
          submitDate: new Date().toISOString().split('T')[0],
          status: 'pending'
        }
        setRegistrations(prev => [...prev, newRegistration])
        return response
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error registering alumni:', err)
      throw err
    }
  }

  // Submit success story - API call (public)
  const submitSuccessStory = async (storyData) => {
    try {
      // Create FormData for image upload
      const formData = new FormData()
      
      // Add all text fields
      Object.keys(storyData).forEach(key => {
        if (key === 'image' && storyData[key] instanceof File) {
          formData.append('image', storyData[key])
        } else if (key === 'achievements' && Array.isArray(storyData[key])) {
          formData.append('achievements', storyData[key].join(','))
        } else if (storyData[key] !== null && storyData[key] !== undefined && storyData[key] !== '') {
          formData.append(key, storyData[key])
        }
      })

      const response = await alumniService.submitStory(formData)
      
      if (response.status === 'success') {
        // Optionally add to local state for immediate feedback
        const newSubmission = {
          ...storyData,
          id: Date.now(),
          submitDate: new Date().toISOString().split('T')[0],
          status: 'pending'
        }
        setStorySubmissions(prev => [...prev, newSubmission])
        return response
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error submitting story:', err)
      throw err
    }
  }

  // LEGACY: Add registration (local storage only)
  const addRegistration = (registration) => {
    const newRegistration = {
      ...registration,
      id: Date.now(),
      submitDate: new Date().toISOString().split('T')[0],
      status: 'pending'
    }
    setRegistrations(prev => [...prev, newRegistration])
    return newRegistration
  }

  // Update registration status (API - admin)
  const updateRegistration = async (id, updates) => {
    try {
      const response = await alumniService.updateRegistration(id, updates)
      
      if (response.status === 'success') {
        // Update local state
        setRegistrations(prev => 
          prev.map(registration => registration._id === id ? { ...registration, ...updates } : registration)
        )
        return response
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error updating registration:', err)
      throw err
    }
  }

  // Delete registration (API - admin)
  const deleteRegistration = async (id) => {
    try {
      await alumniService.deleteRegistration(id)
      setRegistrations(prev => prev.filter(registration => registration._id !== id))
    } catch (err) {
      console.error('Error deleting registration:', err)
      throw err
    }
  }

  const addStorySubmission = (submission) => {
    const newSubmission = {
      ...submission,
      id: Date.now(),
      submitDate: new Date().toISOString().split('T')[0],
      status: 'pending'
    }
    setStorySubmissions(prev => [...prev, newSubmission])
    return newSubmission
  }

  // Update story submission (same as updating a success story)
  const updateStorySubmission = async (id, updates) => {
    try {
      // Story submissions are just inactive success stories
      const response = await alumniService.updateSuccessStory(id, updates)
      
      if (response.status === 'success') {
        // Update success stories state (which will trigger storySubmissions update)
        setSuccessStories(prev => 
          prev.map(story => story._id === id ? { ...story, ...updates } : story)
        )
        return response
      }
      
      throw new Error('Invalid response format')
    } catch (err) {
      console.error('Error updating story submission:', err)
      throw err
    }
  }

  // Delete story submission (same as deleting a success story)
  const deleteStorySubmission = async (id) => {
    try {
      await alumniService.deleteSuccessStory(id)
      // Remove from success stories (which will trigger storySubmissions update)
      setSuccessStories(prev => prev.filter(story => story._id !== id))
    } catch (err) {
      console.error('Error deleting story submission:', err)
      throw err
    }
  }

  // =============== GETTERS ===============

  const getActiveSuccessStories = () => {
    return successStories.filter(story => story.isActive)
  }

  const getFeaturedSuccessStories = () => {
    return successStories.filter(story => story.isActive && story.featured)
  }

  const getActiveTestimonials = () => {
    return testimonials.filter(testimonial => testimonial.isActive)
  }

  const getFeaturedTestimonials = () => {
    return testimonials.filter(testimonial => testimonial.isActive && testimonial.featured)
  }

  const getActiveStatistics = () => {
    return statistics.filter(stat => stat.isActive).sort((a, b) => a.order - b.order)
  }

  const value = {
    // Data
    successStories,
    testimonials,
    statistics,
    registrations,
    storySubmissions,
    loading,
    error,

    // Success Stories CRUD
    addSuccessStory,
    updateSuccessStory,
    deleteSuccessStory,
    submitSuccessStory,  // Public story submission
    approveStorySubmission,  // Admin approval

    // Testimonials CRUD
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,

    // Statistics CRUD
    addStatistic,
    updateStatistic,
    deleteStatistic,

    // Alumni Registration (API)
    registerAlumni,

    // Registration CRUD (API - admin)
    addRegistration,
    updateRegistration,
    deleteRegistration,

    // Story Submission CRUD (API - admin)
    addStorySubmission,
    updateStorySubmission,
    deleteStorySubmission,

    // Getters
    getActiveSuccessStories,
    getFeaturedSuccessStories,
    getActiveTestimonials,
    getFeaturedTestimonials,
    getActiveStatistics
  }

  return (
    <AlumniContext.Provider value={value}>
      {children}
    </AlumniContext.Provider>
  )
}

export const useAlumni = () => {
  const context = useContext(AlumniContext)
  if (!context) {
    throw new Error('useAlumni must be used within an AlumniProvider')
  }
  return context
}

export default AlumniContext